import { createContext, useContext, type ReactNode } from 'react';
import { authClient, useSession } from '../lib/auth-client';

// User type from Better Auth session
interface User {
  id: string;
  name: string | null;
  email: string;
  emailVerified: boolean;
  image?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// Session type from Better Auth
interface Session {
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
  };
  user: User;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isPending: boolean;
  error: Error | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending, error } = useSession();

  const handleSignOut = async () => {
    await authClient.signOut();
  };

  const value: AuthContextType = {
    user: session?.user ?? null,
    session: session ?? null,
    isAuthenticated: !!session?.user,
    isPending,
    error: error ?? null,
    signOut: handleSignOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
